import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import type { Account, AccountStatus } from '@/api';
import { useApi } from '@/api/provider';
import { STALE_TIMES } from '@/api/queryClient';
import { queryKeys } from '@/api/queryKeys';
import { accountAvatarVars, externalProviderName, initialsOf, useAccountContext } from '@/app/accounts';
import { PICKED_MAILBOX_STATE } from '@/app/preferences';
import { useReauthPrompt } from '@/features/credential-notice/reauthPrompt';
import { signsInWithOAuth } from '@/features/oauth-connect/oauth';
import { OUTBOX_SEGMENT } from '@/features/outbox/route';
import { UNIFIED_PATH } from '@/routes/mail/unifiedRoute';
import { ADD_ACCOUNT_FOCUS, REAUTH_ACCOUNT_FOCUS } from '@/routes/settings/sections';
import { cssVars } from '@/utils/css';
import { Icon } from './Icon';
import { lazyComponent } from '@/app/lazyWithPreload';
import { Skeleton } from './Skeleton';

/**
 * Picking a mailbox: make it the active one, and leave the merged list if that
 * is what is on screen.
 *
 * Switching mailboxes from "All inboxes" used to move everything *except* the
 * list. The active account changed, so the sidebar's folder tree, the search
 * box's scope and the switcher's own label all followed it — and the rows did
 * not, because "All inboxes" is a route (ADR 0056 §7) and nothing had navigated
 * away from it. Asking for one mailbox and being shown all of them is the bug
 * the owner reported as "switching … requires a refresh to show".
 *
 * Bare `/mail` rather than that mailbox's Inbox id: the id is a folder list
 * away, and the mail view already resolves the active mailbox's Inbox for
 * exactly this route.
 *
 * Picking a mailbox whose password was rejected also asks for it (#192, plan
 * 06 §6 step 3): opening its mail is the intent the modal waits for, and
 * otherwise the list would only fail to load, quietly.
 */
export function usePickMailbox(): (accountId: string) => void {
  const { accounts, activeAccountId, setActiveAccountId } = useAccountContext();
  const promptReauth = useReauthPrompt();
  const navigate = useNavigate();
  const location = useLocation();
  const onUnified = location.pathname === UNIFIED_PATH || location.pathname.startsWith(`${UNIFIED_PATH}/`);
  // A folder (and a message) id means something only inside one account, so a
  // mail URL naming account A's folder cannot survive a switch to B: it kept
  // A's custom folder in the URL, and the list under it was B asked for a
  // folder it does not have.
  // The Outbox is this device's, not an account's, and stays put.
  const onFolder =
    !onUnified &&
    location.pathname.startsWith('/mail/') &&
    !location.pathname.startsWith(`/mail/${OUTBOX_SEGMENT}`);

  return useCallback(
    (accountId: string) => {
      const switching = accountId !== activeAccountId;
      setActiveAccountId(accountId);
      // Picking the mailbox that is already active still means "show me that
      // mailbox" when the merged list is the one on screen.
      if (onUnified || (switching && onFolder)) navigate('/mail', { state: PICKED_MAILBOX_STATE });
      const picked = accounts.find((account) => account.id === accountId);
      if (picked && !picked.isPrimary && needsPassword(picked)) promptReauth?.(accountId);
    },
    [accounts, activeAccountId, navigate, onFolder, onUnified, promptReauth, setActiveAccountId],
  );
}

/**
 * Each mailbox's Inbox unread count, for the switcher (#193, plan 06 §6:
 * "unread count per account, so switching is informed").
 *
 * Read from the folder list every mailbox already has under
 * `queryKeys.account(id).folders()`: the key the sidebar's tree, the merged
 * view and `FolderProvider` share, and the one the live stream's folder-count
 * frames patch in place (realtime/reducer.ts). So the numbers follow new mail
 * and reads with no request of their own. A list nobody has loaded yet is
 * fetched only while a switcher is open (`load`), once per mailbox per
 * `STALE_TIMES.metadata` -- never per render. A mailbox whose password is not
 * working is never asked (the shell's pollers skip it for the same reason).
 */
export function useInboxUnread(accounts: readonly Account[], load: boolean): ReadonlyMap<string, number> {
  const api = useApi();
  return useQueries({
    queries: accounts.map((account) => ({
      queryKey: queryKeys.account(account.id).folders(),
      queryFn: ({ signal }: { signal: AbortSignal }) => api.folders.list(account.id, signal),
      staleTime: STALE_TIMES.metadata,
      enabled: load && (account.isPrimary || account.status === 'connected'),
    })),
    combine: (results) => {
      const counts = new Map<string, number>();
      results.forEach((result, index) => {
        const account = accounts[index];
        const inbox = result.data?.find((folder) => folder.role === 'inbox');
        if (account && inbox) counts.set(account.id, inbox.unreadCount);
      });
      return counts;
    },
  });
}

/**
 * Sidebar account switcher.
 *
 * One session owner, several connected mailboxes (spec: "Multi-Account
 * Support"). Picking an account swaps the whole mail view, folder tree
 * included — and, from the merged list, leaves it (see `usePickMailbox`).
 */
export function AccountSwitcher() {
  const { accounts, activeAccount, isLoading } = useAccountContext();
  const pickMailbox = usePickMailbox();
  const [open, setOpen] = useState(false);
  const unread = useInboxUnread(accounts, open);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (isLoading) {
    return (
      <div className="account-switcher">
        <div className="account-switcher__trigger">
          <Skeleton width="1.75rem" height="1.75rem" radius="var(--radius-pill)" />
          <div className="account-switcher__meta stack" style={{ gap: 'var(--space-1)' }}>
            <Skeleton width="7rem" height="0.7rem" />
            <Skeleton width="9rem" height="0.6rem" />
          </div>
        </div>
      </div>
    );
  }

  if (!activeAccount) return null;

  const activeIndex = accounts.findIndex((account) => account.id === activeAccount.id);

  return (
    <div className="account-switcher" ref={containerRef}>
      <button
        ref={triggerRef}
        type="button"
        className="account-switcher__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span
          className="avatar"
          style={cssVars(accountAvatarVars(activeAccount, activeIndex))}
        >
          {initialsOf(activeAccount.displayName || activeAccount.email)}
        </span>
        <span className="account-switcher__meta">
          <span className="account-switcher__name truncate">{activeAccount.displayName}</span>
          <span className="account-switcher__email truncate">{activeAccount.email}</span>
        </span>
        <AccountStatusDot account={activeAccount} />
        <Icon name="chevron-down" size={16} />
      </button>

      {open && (
        <div className="account-switcher__menu" role="menu" aria-label="Connected accounts">
          {accounts.map((account, index) => (
            <Fragment key={account.id}>
              <button
                type="button"
                role="menuitemradio"
                aria-checked={account.id === activeAccount.id}
                className="account-switcher__item"
                onClick={() => {
                  // Back to the button before picking: the menu closes under
                  // the item, and a password modal the pick opens (#192)
                  // gives focus back to whatever had it.
                  setOpen(false);
                  triggerRef.current?.focus();
                  pickMailbox(account.id);
                }}
              >
                <AccountSummary account={account} index={index} unread={unread.get(account.id)} />
              </button>
              {needsPassword(account) && (
                <ReenterPasswordLink
                  account={account}
                  className="account-switcher__item account-switcher__reauth"
                  role="menuitem"
                  onNavigate={() => setOpen(false)}
                />
              )}
            </Fragment>
          ))}
          {/*
            A link, not a button with a handler: this navigates, so it should
            behave like navigation -- middle-click, Ctrl-click and the status
            bar all work, and the keyboard gets it for free.

            It carried a `TODO(api)` and NO onClick at all, so it did nothing
            whatsoever when clicked. The handheld sheet below has always done
            this correctly; only the desktop menu was never wired up.
          */}
          <Link
            to="/settings/accounts"
            state={{ focus: ADD_ACCOUNT_FOCUS }}
            className="account-switcher__item"
            role="menuitem"
            onClick={() => setOpen(false)}
          >
            <span className="avatar" style={cssVars({ '--avatar-color': 'var(--color-bg-sunken)', '--avatar-ink': 'var(--color-text-muted)' })}>
              <Icon name="plus" size={14} />
            </span>
            <span className="account-switcher__meta">
              <span className="account-switcher__name">Add account</span>
            </span>
          </Link>
        </div>
      )}
    </div>
  );
}

/**
 * Avatar, name, email, Inbox unread count and status dot: one account, as both
 * menus list it. The count is in words for a screen reader ("26 unread"), and
 * absent rather than "0" when there is none or it is not known yet.
 */
export function AccountSummary({ account, index, unread }: { account: Account; index: number; unread?: number | undefined }) {
  return (
    <>
      <AccountAvatar account={account} index={index} />
      <span className="account-switcher__meta">
        <span className="account-switcher__name truncate">{account.displayName}</span>
        <span className="account-switcher__email truncate">{account.email}</span>
      </span>
      <ProviderBadge account={account} />
      {unread !== undefined && unread > 0 && (
        <span className="badge account-switcher__unread">
          {unread}
          <span className="visually-hidden"> unread</span>
        </span>
      )}
      <AccountStatusDot account={account} />
    </>
  );
}

/**
 * "Gmail" beside a mailbox on another provider (ADR 0107), in words: the
 * provider is the reason it has no filters, calendar or contacts, so it is
 * said, not coloured. Read with the row as "… on Gmail". Nothing for ours.
 */
export function ProviderBadge({
  account,
  className,
}: {
  account: Pick<Account, 'provider' | 'providerName'>;
  className?: string;
}) {
  const name = externalProviderName(account);
  if (name === undefined) return null;
  return (
    <span className={className ? `badge account-provider-badge ${className}` : 'badge account-provider-badge'}>
      <span className="visually-hidden"> on </span>
      {name}
    </span>
  );
}

/** Words for each status: the dot's accessible name and its tooltip. */
const STATUS_LABELS: Record<AccountStatus, string> = {
  connected: 'Connected',
  connecting: 'Connecting',
  degraded: 'Degraded, retrying',
  error: 'Error',
  'reauth-required': 'Needs password',
};

/** A mailbox whose saved password the host rejected; only the user can fix it. */
export function needsPassword(account: Account): boolean {
  return account.status === 'reauth-required';
}

/**
 * The coloured status dot, with its state in words.
 *
 * It was a bare `<span>` with a `title`, so a screen reader heard nothing and
 * the stylesheet knew only `error` and `connecting` -- a mailbox waiting on
 * its password fell through to green (#192/#193).
 */
export function AccountStatusDot({ account }: { account: Account }) {
  // A Google or Microsoft mailbox (ADR 0109) has no password to ask for.
  const label =
    account.status === 'reauth-required' && signsInWithOAuth(account) ? 'Needs sign-in' : STATUS_LABELS[account.status];
  return (
    <span
      className="account-status-dot"
      data-status={account.status === 'connected' ? 'ok' : account.status}
      role="img"
      aria-label={label}
      title={account.statusMessage ?? label}
    />
  );
}

/**
 * "Re-enter password", under a mailbox that needs it.
 *
 * Inside the shell it opens the password modal over the app (#192); the link
 * to the form on that mailbox's row in Settings → Accounts stays as its href,
 * for a modified click and for anywhere without the shell (a popped-out
 * window, a bare component). It is offered wherever the amber dot is seen (the
 * switcher, the phone sheet, the sidebar's mailbox branch, the shell banner).
 * A sibling of the row rather than inside it: the row is a button, and a
 * control nested in a button is unreachable.
 */
export function ReenterPasswordLink({
  account,
  className,
  role,
  onNavigate,
}: {
  account: Account;
  className: string;
  role?: 'menuitem';
  onNavigate?: () => void;
}) {
  const promptReauth = useReauthPrompt();
  // A Google or Microsoft mailbox (ADR 0109) is signed in again, never given a password.
  const oauth = signsInWithOAuth(account);
  return (
    <Link
      to="/settings/accounts"
      state={{ focus: REAUTH_ACCOUNT_FOCUS, accountId: account.id }}
      className={className}
      {...(role ? { role } : {})}
      {...(promptReauth ? { 'aria-haspopup': 'dialog' as const } : {})}
      aria-label={oauth ? `Sign in again to ${account.email}` : `Re-enter password for ${account.email}`}
      onClick={(event) => {
        const modified = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;
        if (promptReauth && !modified) {
          event.preventDefault();
          // Focus goes back to what was under the menu, not to this link,
          // which closes with it.
          promptReauth(account.id, onNavigate ? null : event.currentTarget);
        }
        onNavigate?.();
      }}
    >
      {oauth ? 'Sign in again' : 'Re-enter password'}
    </Link>
  );
}

/** The account's initials on its swatch colour. */
export function AccountAvatar({
  account,
  index,
  large = false,
}: {
  account: Account;
  index: number;
  large?: boolean;
}) {
  return (
    <span
      className={large ? 'avatar avatar--lg' : 'avatar'}
      style={cssVars(accountAvatarVars(account, index))}
      aria-hidden="true"
    >
      {initialsOf(account.displayName || account.email)}
    </span>
  );
}

/**
 * The handheld account switcher's bottom sheet, loaded the first time it
 * opens (#577): the sheet and its list are a chunk of their own, prefetched at
 * idle, so a desktop session never pays for them. Kept mounted after the first
 * open so it can animate closed.
 */
export const LazyAccountSheet = lazyComponent(() => import('./AccountSheet'), 'AccountSheet');

export function AccountSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [opened, setOpened] = useState(open);
  if (open && !opened) setOpened(true);
  if (!opened) return null;
  return <LazyAccountSheet open={open} onClose={onClose} />;
}
